import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar, ToastContainer } from '../components';
import type { SidebarOption } from '../components';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { useAuditStore } from '../store/auditStore';
import { canAccessRoute } from '../utils/rbac';
import type { AuditModule } from '../types/auditLog';
import {
  PersonIcon,
  LayersIcon,
  IdCardIcon,
  GroupIcon,
  GearIcon,
  BellIcon,
  HamburgerMenuIcon,
  Cross1Icon,
} from '@radix-ui/react-icons';
import './DashboardLayout.scss';

const dashboardNavIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none">
    <rect fill="currentColor" x="3" y="3" width="5" height="5" rx="1" />
    <rect fill="currentColor" x="12" y="3" width="5" height="5" rx="1" />
    <rect fill="currentColor" x="3" y="12" width="5" height="5" rx="1" />
    <rect fill="currentColor" x="12" y="12" width="5" height="5" rx="1" />
  </svg>
);

const leadManagementIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none">
    <path
      d="M10 2.5L11.8 7.2L16.5 7.2L12.85 10.05L14.2 14.8L10 12.2L5.8 14.8L7.15 10.05L3.5 7.2L8.2 7.2L10 2.5Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path d="M4 16.5H16" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M7 13.5H13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const kanbanIcon = (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.33333 11.6667H5V3.33333H3.33333V11.6667ZM10 10H11.6667V3.33333H10V10ZM6.66667 7.5H8.33333V3.33333H6.66667V7.5ZM1.66667 15C1.20833 15 0.815972 14.8368 0.489583 14.5104C0.163194 14.184 0 13.7917 0 13.3333V1.66667C0 1.20833 0.163194 0.815972 0.489583 0.489583C0.815972 0.163194 1.20833 0 1.66667 0H13.3333C13.7917 0 14.184 0.163194 14.5104 0.489583C14.8368 0.815972 15 1.20833 15 1.66667V13.3333C15 13.7917 14.8368 14.184 14.5104 14.5104C14.184 14.8368 13.7917 15 13.3333 15H1.66667ZM1.66667 13.3333H13.3333V1.66667H1.66667V13.3333ZM1.66667 1.66667V13.3333V1.66667Z" fill="currentColor" />
  </svg>
);

const ROUTE_MODULE_MAP: Record<string, AuditModule> = {
  '/dashboard': 'dashboard',
  '/kanban': 'kanban',
  '/users': 'users',
  '/leads': 'leads',
  '/employees': 'employees',
  '/plans': 'plans',
  '/user-plans': 'invoices',
  '/settings': 'settings',
  '/notifications': 'notifications',
  '/audit-logs': 'settings',
};

function resolveModule(pathname: string): AuditModule | null {
  for (const [prefix, mod] of Object.entries(ROUTE_MODULE_MAP)) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return mod;
  }
  return null;
}

const auditLogsIcon = (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 2.5A.5.5 0 0 1 2.5 2h10a.5.5 0 0 1 0 1h-10A.5.5 0 0 1 2 2.5ZM2 5.5A.5.5 0 0 1 2.5 5h10a.5.5 0 0 1 0 1h-10A.5.5 0 0 1 2 5.5ZM2.5 8a.5.5 0 0 0 0 1H8a.5.5 0 0 0 0-1H2.5ZM2 11.5a.5.5 0 0 1 .5-.5H6a.5.5 0 0 1 0 1H2.5a.5.5 0 0 1-.5-.5Z" fill="currentColor"/>
    <circle cx="11.5" cy="10.5" r="2.5" stroke="currentColor" strokeWidth="1"/>
    <path d="M13.5 12.5L15 14" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
  </svg>
);

const ALL_SIDEBAR_OPTIONS = (unreadCount: number): SidebarOption[] => [
  { key: 'dashboard', label: 'Dashboard', icon: dashboardNavIcon, path: '/dashboard' },
  { key: 'kanban', label: 'Kanban Board', icon: kanbanIcon, path: '/kanban' },
  { key: 'users', label: 'Client Profiles', icon: <PersonIcon />, path: '/users' },
  { key: 'leads', label: 'Lead Management', icon: leadManagementIcon, path: '/leads' },
  { key: 'employees', label: 'Employee Management', icon: <GroupIcon />, path: '/employees' },
  { key: 'user-plans', label: 'Invoices', icon: <IdCardIcon />, path: '/user-plans' },
  { key: 'plans', label: 'Subscriptions', icon: <LayersIcon />, path: '/plans' },
  { key: 'settings', label: 'Settings', icon: <GearIcon />, path: '/settings', group: 'secondary' },
  {
    key: 'audit-logs',
    label: 'Audit Logs',
    icon: auditLogsIcon,
    path: '/audit-logs',
    group: 'secondary',
  },
  {
    key: 'notifications',
    label: 'Notifications',
    icon: <BellIcon />,
    path: '/notifications',
    group: 'secondary',
    badge: unreadCount > 0 ? unreadCount : undefined,
  },
];

const buildSidebarOptions = (
  unreadCount: number,
  role: string | null,
  hasPermission: (permission: string) => boolean,
): SidebarOption[] =>
  ALL_SIDEBAR_OPTIONS(unreadCount).filter((option) => {
    if (!canAccessRoute(role, option.key)) return false;
    if (option.key === 'audit-logs' && !hasPermission('audit_log_mgmt:read')) return false;
    return true;
  });

export const DashboardLayout: React.FC = () => {
  const logout = useAuthStore(state => state.logout);
  const user = useAuthStore(state => state.user);
  const role = useAuthStore(state => state.role);
  const hasPermission = useAuthStore(state => state.hasPermission);
  const unreadCount = useNotificationStore(state => state.unreadCount);
  const fetchUnreadCount = useNotificationStore(state => state.fetchUnreadCount);
  const logEvent = useAuditStore(state => state.logEvent);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    void fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Log page navigation events (skip first render which is the initial login redirect)
  useEffect(() => {
    const pathname = location.pathname;
    if (prevPathRef.current === pathname) return;
    prevPathRef.current = pathname;

    const module = resolveModule(pathname);
    if (module) {
      logEvent({ action: 'PAGE_VIEW', module });
    }
  }, [location.pathname, logEvent]);

  const sidebarOptions = buildSidebarOptions(unreadCount, role, hasPermission);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  return (
    <div className="dashboard-layout">
      <ToastContainer />
      {/* Mobile Menu Toggle */}
      <button className="mobile-menu-toggle" onClick={toggleSidebar} aria-label="Toggle menu">
        {isSidebarOpen ? <Cross1Icon /> : <HamburgerMenuIcon />}
      </button>

      {/* Sidebar Overlay for mobile */}
      <div
        className={`sidebar-overlay ${isSidebarOpen ? 'sidebar-overlay--visible' : ''}`}
        onClick={closeSidebar}
      />

      <div className={`sidebar-container ${isSidebarOpen ? 'sidebar-container--open' : ''}`}>
        <Sidebar
          options={sidebarOptions}
          onLogout={logout}
          onNavigate={closeSidebar}
          user={{
            name: user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(' '),
            email: user?.email,
            avatar: user?.avatar,
            role: role ?? undefined,
          }}
        />
      </div>
      <main className="dashboard-layout__main">
        <Outlet />
      </main>
    </div>
  );
};
